import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { ApiError } from '@/deploy/services/api';
import type { ApiService } from '@/deploy/services/api-services.service';
import { DATABASE_CONFIRM_CODE, databaseNameMatches } from '@/deploy/utils/apiEnvs';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';

// ============================================================
// API de base de données (MongoDB...) : changer son image peut l'empêcher de redémarrer sur ses
// données. Release, « Déployer une version… » et la création d'un serveur demandent de retaper
// son nom exact, envoyé au CICD dans confirmDatabase (un CICD plus ancien ignore le champ).
// ============================================================

/** Badge discret de la ligne d'une API de base de données. */
export function DatabaseBadge() {
  return (
    <span title="Base de données : Release et « Déployer une version… » demandent de retaper son nom, la promotion ne la met pas à jour">
      <Badge tone="muted">Base de données</Badge>
    </span>
  );
}

/**
 * Encadré de confirmation : le risque, puis le nom de l'API à retaper. Le bouton de validation
 * de l'appelant reste désactivé tant que `databaseNameMatches(api, value)` est faux.
 */
export function DatabaseConfirmField({ api, summary, value, onChange, disabled, autoFocus }: {
  api: Pick<ApiService, 'name'>;
  /** Première phrase : ce qui change l'image (« La Release en v8.0.4 sur Prod change l'image de PanelMongo. »). */
  summary: ReactNode;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const id = useId();
  const typed = value.trim() !== '';
  const ok = databaseNameMatches(api, value);
  return (
    <div className={pageStyles.dbConfirm} role="group" aria-labelledby={`${id}-title`}>
      <div id={`${id}-title`} className={pageStyles.dbConfirmTitle}>
        <Icon name="warn" size={13} /> Base de données
      </div>
      <p className={pageStyles.dbConfirmText}>
        {summary} Sur une base de données, un saut de version peut l'empêcher de redémarrer sur ses données
        (MongoDB : featureCompatibilityVersion). Vérifie la version visée avant de confirmer.
      </p>
      <label className={pageStyles.field}>
        <span>Nom de l'API, à retaper pour confirmer</span>
        <input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} autoFocus={autoFocus}
          autoComplete="off" spellCheck={false} aria-invalid={typed && !ok}
          aria-describedby={`${id}-hint`} />
      </label>
      <div id={`${id}-hint`}
        className={[pageStyles.fieldHint, typed && !ok && pageStyles.fieldHintDanger].filter(Boolean).join(' ')}>
        {ok ? 'Nom confirmé.' : <>Tape <code>{api.name}</code> exactement, majuscules comprises.</>}
      </div>
    </div>
  );
}

/** Confirmation d'une Release (ou d'un Rollback) d'une API de base de données, avant tout appel. */
export function DatabaseConfirmModal({ api, title, summary, confirmLabel, onConfirm, onClose }: {
  api: Pick<ApiService, 'name'>;
  title: string;
  summary: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  const ok = databaseNameMatches(api, typed);
  return (
    <Modal
      open onClose={onClose}
      title={title}
      subtitle="Arrêt puis redémarrage sur la nouvelle image"
      width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button variant="danger" onClick={onConfirm} disabled={!ok} leftIcon={<Icon name="rocket" size={12} />}>
          {confirmLabel}
        </Button>
      </>}
    >
      <form className={pageStyles.form} onSubmit={(e) => { e.preventDefault(); if (ok) onConfirm(); }}>
        <DatabaseConfirmField api={api} summary={summary} value={typed} onChange={setTyped} autoFocus />
      </form>
    </Modal>
  );
}

/**
 * 409 DATABASE_CONFIRM_REQUIRED : le panel n'a pas reconnu l'API comme base de données (CICD plus
 * récent que le panel) ou son nom a changé entre-temps. Message à afficher, null pour toute autre erreur.
 */
export function databaseConfirmErrorMessage(e: unknown, api: Pick<ApiService, 'name'>): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const code = (e.body as { code?: unknown } | undefined)?.code;
  if (code !== DATABASE_CONFIRM_CODE) return null;
  return `Le CICD traite ${api.name} comme une base de données et n'a pas reçu son nom exact : recharge la page`
    + " (panel ou nom de l'API pas à jour), puis relance l'action. Rien n'a été changé.";
}
